import type { AuthId, CalendarId, EventId } from "@tento-lona/sheets";
import type { Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import { EventCellWrite } from "../events/event-cell-write.ts";
import { DateTime, Utc } from "@tento-chrono";
import { EventItem, EventItemAllDay, EventItemTimed } from "../events/event-item.ts";
import { EventItemPresentation } from "../events/event-item-view.ts";
import { EventTimezoneChanged } from "../invalidation.ts";
import { Network } from "../network.ts";

import { emitEventUpdate, EventsTable, type EventsRowState } from "./events-table.ts";
import type { Actor, CellValue, Json } from "@tento-lona/sheets";
import { CalendarEvent } from "@tento-lona/sheets";
import { CellId, EventOrigin, EventPatch, RowKey, RowNetworkKey } from "@tento-lona/sheets";
import { MutationClientId } from "@tento-lona/sheets/mutation";
import type { RowCellCache } from "../sync/row-cell-cache.ts";
import { Tracing } from "@tento-core/tracing";
import { EventOverlayProjector } from "../mutation/event-overlay-projector.ts";
import { EventWriteError, SdkRequestError } from "../errors.ts";
import { ClientDisposedError, SheetHandleDisposedError } from "../client/handle-errors.ts";
import { Durable, DurableHandoffs } from "../client/durable-handoffs.ts";
import type { DebouncedRequests } from "@tento-core/debounced-requests";
import { EVENT_SHAPE } from "@tento-lona/sheets/platform";

const CELLS_PATH = "/api/rows/{rowKey}/cells";

/**
 * The debounce key of a durable update: the row, the event, and the sorted
 * set of fields the patch writes. Two durable writes coalesce (the executor
 * sends only the later body) only when they write the same fields, so the
 * body that is sent always carries every field of the bodies it replaced.
 */
function durableKey(
  rowKey: RowNetworkKey,
  id: string,
  patch: Network.EventUpdate,
): string {
  const fields = EventOverlayProjector.writtenFields(patch);
  return `event-update:${rowKey}:${id}:${fields.join(",")}`;
}

/**
 * The projector correlation id of an optimistic placeholder's insert op: the
 * placeholder's own event id, so `create` confirms (and a mid-flight delete
 * drops) the op by the id the placeholder shows.
 */
function placeholderClientId(id: EventId): MutationClientId {
  return MutationClientId.of(id);
}

/** An update applied optimistically and not yet settled (see `applyUpdate`). */
type PreparedUpdate = {
  /** The event with the patch applied — what the cell now shows. */
  readonly updated: EventItem;
  /** The row the write is POSTed to and projected onto. */
  readonly rowKey: RowNetworkKey;
  /** The projector op to confirm or drop once the write settles. */
  readonly clientId: MutationClientId;
  /** The field-level op itself — what `confirmUpdate` folds on success. */
  readonly op: EventOverlayProjector.UpdateOp;
  readonly body: Network.CellWriteRequest;
};

/**
 * Write-side handle for events on a specific row. Sibling to
 * `row.as(EVENTS_FACADE)`, which carries reads only (the facade's own
 * cell-level writers are the rows side's `eventsWriterOf`, on
 * `@tento-lona/sheets/internal-sdk`). SDK-internal: the events
 * client resolves one per writable source row (`EventsClient.opsForOrigin`),
 * and app code writes through `client.events`.
 *
 * Mutation methods write to the row's canonical cell store + POST
 * to the cells route. Pulls primitives from the per-row
 * `EventsRowState` attached by `EventsTable.attachRow()` — the
 * table itself owns api / invalidation / shared storage.
 *
 * Why read/write split? `EVENTS_FACADE` is the queryable read
 * projection on the sheets-side row; `EventOps` is the typed
 * write capability on the SDK-side row. Splitting keeps the
 * facade pure-read and makes "what mutates this row?" a single
 * point.
 *
 * Gestures and actions write through `client.events` (`update`,
 * `updateDurable` for a drag settle), which routes to this class — no
 * `this.sheet` reference.
 */
export class EventOps {
  readonly #table: EventsTable;
  readonly #state: EventsRowState;
  private constructor(
    table: EventsTable,
    state: EventsRowState,
  ) {
    this.#state = state;
    this.#table = table;
  }

  /**
   * The apply-mode overlay projector (SDK 2.0 Move-4): the overlay is the
   * authoritative optimistic state and its derived snapshot is projected to the
   * cell store (which every renderer still reads).
   * Shared per-table via `ensureOverlayProjector` (the projector keys its
   * per-row overlays by rowKey), and wired here with the table's lane sink
   * (`applyOptimisticEdit` / `applyOptimisticDelete` over `writeCanonical`) and
   * its invalidation emit. The sink resolves the target row's state by rowKey,
   * so a single projector serves every row (incl. cross-calendar copy/move).
   */
  #projector(): EventOverlayProjector {
    return this.#table.ensureOverlayProjector(() =>
      new EventOverlayProjector(
        (rowKey, write) => {
          const st = this.#table.getRowStateByKey(rowKey);
          if (!st) {
            // Unattached canonical (source) row — e.g. a create onto a
            // per-calendar `:~google:…:events` row that isn't attached to this
            // EventsTable (only the `:~lona:` aggregator is). `canonicalCells`
            // is keyed by lookupKey (no RowCellCache needed), so write it
            // directly: the month view reads canonicalCells and the forward
            // bridge fans the same write to the week view. This closes the
            // Move-3 create-live gate — projecting onto an unattached row used
            // to no-op, so the create didn't paint until a reload.
            // `writeCanonicalForKey` no-ops for non-canonical keys, preserving
            // the prior `return` behavior there.
            //
            // Also mirror into the global id→item index (`ingestCanonicalForKey`)
            // so the freshly-rendered event is *resolvable* by id — a follow-up
            // right-click / details edit·delete·move (`RowEvents.requireEvent` →
            // `itemFor`) on it must not miss. This makes `itemFor` authoritative
            // for what canonicalCells renders (no double-render — see the method
            // doc), which is what lets `RowEvents.peekInTable` drop its reader
            // fallback (Move-3).
            if (write.value != null || write.prev != null) {
              this.#table.writeCanonicalForKey(
                rowKey,
                write.value?.inner ?? null,
                write.id,
                write.prev?.inner ?? null,
              );
              this.#table.ingestCanonicalForKey(
                rowKey,
                write.value?.inner ?? null,
                write.id,
              );
            }
            return;
          }
          if (write.value != null) {
            this.#table.applyOptimisticEdit(
              st,
              write.value.inner,
              write.id,
              write.prev?.inner ?? null,
            );
          } else if (write.prev != null) {
            this.#table.applyOptimisticDelete(st, write.prev.inner, write.id);
          }
        },
        (writes, actor) => this.#table.emitForWrites(writes, actor),
        EventOverlayProjector.DEFAULT_CLOCK,
        EventOverlayProjector.SETTLE_DEADLINE_MS,
      )
    );
  }

  /**
   * The row a NEW event (create / copyTo / insertOptimistic) lands on, plus its
   * origin — the event's calendar (`authId`/`calendarId`) resolved to its
   * SERVER-RENDERED events-row key (`EventsTable.eventsLookupKeyFor`), i.e. the
   * SAME row the POST writes. This can differ from
   * this ops' own `state.rowKey`: a canonical/default ops creating onto a
   * specific Google calendar, or a recurring "this"/"following" fork whose
   * detached event belongs to the master's calendar. The optimistic projection
   * MUST target this row — projecting onto `state.rowKey` instead lands the
   * optimistic cell on a row nothing renders, so the create doesn't paint until
   * a reload. Falls back to this ops' row when the target isn't attached (the
   * sink then no-ops the optimistic write; the server POST still lands).
   */
  #newEventTarget(
    authId: AuthId,
    calendarId: CalendarId,
  ): { rowKey: RowNetworkKey; origin: EventOrigin } {
    const rowKey = this.#table.eventsLookupKeyFor(
      authId,
      calendarId,
      "newEventTarget",
    );
    const localId = this.#table.getRowStateByKey(rowKey)?.rowLocalId ??
      this.#state.rowLocalId;
    return { rowKey, origin: EventOrigin.backingRow(localId) };
  }

  /** Resolve EventOps from a RowCellCache. Returns null when the row
   *  isn't attached to any `EventsTable` (i.e., not event-bearing). */
  static fromHandle(rh: RowCellCache): Option<EventOps> {
    const table = EventsTable.fromHandle(rh);
    if (!table) return null;
    const state = table.getRowState(rh);
    if (!state) return null;
    return new EventOps(table, state);
  }

  /** Build EventOps directly from an attached row's state. Used to
   *  resolve a writable handle by row key (origin-routed writes)
   *  without holding the row's `RowCellCache`. */
  static fromState(table: EventsTable, state: EventsRowState): EventOps {
    return new EventOps(table, state);
  }

  // ── Queries (preserved for the optimistic-flow check) ──────────

  /** True if an event with `id` is in the table. Used by the
   *  create-event optimistic-flow to gate a late-server-response
   *  ingest against an already-removed placeholder. */
  has(id: string): boolean {
    return this.#table.has(id);
  }

  /** Resolve an event from the client-scoped table by id — the global
   *  index across every attached row, not a single row's reader slice.
   *  Returns null if absent. `RowEvents.requireEvent` falls back to this
   *  when the per-row reader misses an event that is nonetheless live in
   *  the table (the standalone-row write path opening an unfetched
   *  handle for an event loaded via the calendar-list aggregate). */
  find(id: string): Option<EventItem> {
    return this.#table.itemFor(id);
  }

  /** Resolve an event from the client-scoped table by id — the Move-2 id→item
   *  authority. `RowEvents.requireEvent` uses this as the primary resolve. */
  itemFor(id: string): Option<EventItem> {
    return this.#table.itemFor(id);
  }

  /**
   * Resolve the recurring MASTER for `masterId`, fetching the source row's
   * cells around `near` if it isn't indexed yet.
   *
   * Why this exists: a source row opened purely to dispatch a write (via
   * `EventsClient.opsForOrigin`) carries row meta but no cells — `RowsClient.
   * open` fetches `/api/rows/{rowKey}` (meta) and cells load lazily per
   * viewport. So a recurring master that renders through the calendar-list
   * aggregate (a different, already-loaded handle) is NOT yet in this freshly
   * opened handle's slice. EXDATE/UNTIL edits ("delete this/following
   * occurrence") need the real master as the optimistic `prev`: feeding the
   * occurrence instead writes an occurrence-shaped cell under the master id at
   * the occurrence's date, which (custom store is id-keyed) MOVES the master's
   * own cell off its DTSTART — the "deletes the wrong event" corruption.
   *
   * Fetching `near`'s day surfaces the master: Google returns a recurring
   * master for any range its series overlaps, so the occurrence's own day
   * pulls it in. Returns null only if it still can't be resolved (caller must
   * NOT fall back to the occurrence).
   *
   * `near` may BE the master: a series' FIRST occurrence renders from the
   * master's own cell (the expander skips DTSTART — see `expandRecurrenceUntil`),
   * so "delete/edit ALL" clicked there hands us the master itself. That held
   * item is authoritative and needs no index hit, which matters because
   * `itemFor` is NOT authoritative for everything that renders: the renderer
   * draws from the sheets `EventsIndex`, and an id ingested from two source rows
   * is evicted globally by a `clearRow` on either. Without this branch the first
   * occurrence resolved to null while every later occurrence resolved fine
   * (measured 2026-08-12) — and the recovery `loadRange` below cannot repair it,
   * since the fetcher returns early for an already-covered range.
   */
  async resolveMaster(
    masterId: EventId,
    near: EventItem,
  ): Promise<Option<EventItem>> {
    const existing = this.#table.itemFor(masterId);
    if (existing) return existing;
    if (EventOps.#isMasterOf(near, masterId)) return near;
    await this.#table.ensureLoaded(this.#state, EventOps.#dayRangeOf(near));
    const fetched = this.#table.itemFor(masterId);
    if (!fetched) {
      // The caller turns this into a throw that a dismissing dialog swallows,
      // so record it here — otherwise a resolve miss is indistinguishable from
      // a delete the user never confirmed.
      Tracing.event("warn", "event-ops/resolve-master.miss", {
        masterId,
        nearId: near.id,
        rowKey: this.#state.rowKey,
      });
    }
    return fetched;
  }

  /** True when `item` is itself the recurring master identified by `masterId`
   *  — its own cell carries the RRULE and points at no parent. */
  static #isMasterOf(item: EventItem, masterId: EventId): boolean {
    const raw = item.inner;
    return raw.id === masterId && raw.recurrence != null &&
      raw.recurringEventId == null;
  }

  /** The UTC day-range covering an event's start — used to fetch a recurring
   *  master via one of its occurrences (the series overlaps that day). */
  static #dayRangeOf(item: EventItem): DateTime.Range<Utc> {
    const start = item.inner.start ??
      (item.inner.startDate != null
        ? new DateTime(item.inner.startDate.withTime(), Utc)
        : null);
    if (!start) {
      throw new Error("EventOps.dayRangeOf: event missing start");
    }
    const startOfDay = DateTime.fromDse(start.date.dse, Utc);
    return new DateTime.Range(startOfDay, startOfDay.add({ days: 1 }));
  }

  // ── Mutations ──────────────────────────────────────────────────

  /** Optimistic local insert; no server roundtrip. The caller
   *  receives a hydrated `EventItem` whose id is a synthetic
   *  `optimistic-event-N` so a subsequent `create` round-trip can
   *  reconcile against it. */
  insertOptimistic(
    authId: AuthId,
    calendarId: CalendarId,
    event: Network.NewEvent,
    actor: Actor,
  ): EventItem {
    const start = event.start ?? (event.startDate
      ? new DateTime(event.startDate.withTime(), Utc)
      : null);
    const end = event.end ?? (event.endDate
      ? new DateTime(event.endDate.withTime(), Utc)
      : null);
    if (!start || !end) {
      throw new Error(
        "EventOps.insertOptimistic: event shape missing time fields",
      );
    }
    const ev = CalendarEvent.of({
      id: this.#table.generateOptimisticId(),
      authId,
      calendarId,
      title: event.title ?? "",
      start,
      end,
      ...(event.startDate != null ? { startDate: event.startDate } : {}),
      ...(event.endDate != null ? { endDate: event.endDate } : {}),
      ...(event.description != null ? { description: event.description } : {}),
      ...(event.category != null ? { category: event.category } : {}),
      ...(event.suggestedCategory != null
        ? { suggestedCategory: event.suggestedCategory }
        : {}),
      ...(event.useSuggestedCategory != null
        ? { useSuggestedCategory: event.useSuggestedCategory }
        : {}),
      ...(event.recurrence != null ? { recurrence: event.recurrence } : {}),
      ...(event.timezone != null ? { timezone: event.timezone } : {}),
      ...(event.location != null ? { location: event.location } : {}),
      // The user just created this, so it's editable immediately — without this
      // the optimistic placeholder fails `isEditable` (creatorIsSelf) and opens
      // read-only (no edit/save bar) until the server reconcile lands.
      creatorIsSelf: true,
    });

    // Overlay-authoritative: append an insert op keyed by the placeholder id (so
    // `create` can confirm/drop the same op); the projector writes the cell
    // lane-correctly + mirrors the canonical lane from one derivation. Target the
    // event's OWN calendar row (`newEventTarget`), not this ops' `state.rowKey` —
    // they differ when a default ops creates onto a specific calendar.
    const { rowKey, origin } = this.#newEventTarget(authId, calendarId);
    const item = EventItem.of(ev, origin);
    this.#projector().apply(
      rowKey,
      this.#table.rowSnapshot(rowKey),
      { kind: "insert-event", item },
      placeholderClientId(item.id),
      actor,
    );
    return item;
  }

  /** Create an event — POSTs to the cells route, returns the
   *  hydrated server-side event.
   *
   *  `reconcile` is the optimistic placeholder from a preceding
   *  `insertOptimistic`, or null for plain creates. The placeholder is
   *  reconciled HERE, atomically with the canonical ingest, because the
   *  caller can't do it reliably: on single-cardinality rows the
   *  canonical cell lands on the placeholder's date key and evicts it
   *  from the table, so a post-create `has(placeholder.id)` check reads
   *  false and the caller misclassifies the eviction as "the user
   *  deleted the placeholder mid-flight" — then deletes the freshly
   *  created event while the placeholder's renderer mirror lives on
   *  (the "both cells" create bug). Presence is captured before the
   *  ingest, and the placeholder is dropped from every store using the
   *  item the caller holds — no table lookup. */
  async create(
    authId: AuthId,
    calendarId: CalendarId,
    event: Network.NewEvent,
    reconcile: Option<EventItem>,
    actor: Actor,
  ): Promise<EventItem> {
    // Project optimism onto the event's OWN calendar row (the POST target), not
    // this ops' `state.rowKey` — see `newEventTarget`. Mismatch = no live paint.
    const { rowKey, origin } = this.#newEventTarget(authId, calendarId);
    const target = EventCellWrite.target(rowKey, authId, calendarId);
    // The Google events handler derives event times from `data.newEvent`, so
    // the outer cell `date` is the cell's storage key, not the event's
    // authority — but it must be the event's REAL day, never a fabricated "now".
    // Create omits the id by design and the body deserializer rejects a
    // `SemanticTime::Point` cell without an id (cardinality contract,
    // `tento-lona-connector/src/cell.rs:73`), so `eventCellDate(_, _, "day")`
    // emits a `Range`-shape day bucket.
    // after-failed-write: the ops layer's caller recovers — EventsClient.create re-reads the calendar's row.
    const resp = await EventWriteError.check(
      "create",
      "POST",
      CELLS_PATH,
      () =>
        this.#table.api.postResult(CELLS_PATH, {
          path: { rowKey: RowNetworkKey.fromWire(rowKey) },
          body: Network.CellWriteRequest.make({
            date: EventsTable.eventCellDate(
              event.startDate ?? null,
              event.start ?? null,
              "day",
            ),
            data: EventCellWrite.create(target, event),
            endDate: null,
            id: null,
          }),
        }),
    );
    const raw = resp.cells[0]?.data;
    if (raw == null) {
      throw malformedAck("create", "create: the ack carried no cell");
    }
    const canonical = canonicalEvent(this.#state.rh?.decodeCell(raw));
    if (!canonical) {
      throw malformedAck("create", "create: the ack's cell is not an event");
    }

    // Overlay-authoritative reconcile. The placeholder's insert op (appended by
    // `insertOptimistic` under `reconcile.id`) is confirmed with the server
    // canonical — the projector's diff re-keys optimistic → real id (clear the
    // placeholder cell, write the canonical) in one derivation.
    const canonicalItem = EventItem.of(canonical, origin);
    if (reconcile != null) {
      if (this.#projector().hasPending(rowKey, placeholderClientId(reconcile.id))) {
        // Record the ghost promotion BEFORE the confirm fires its
        // invalidation, so the promoting repaint can re-key the ghost's DOM
        // element in place instead of tearing it down (see
        // `EventIdPromotions`).
        this.#table.promotions.record(reconcile.id, canonicalItem.id);
        this.#projector().confirm(rowKey, placeholderClientId(reconcile.id), {
          kind: "upsert",
          id: canonicalItem.id,
          item: canonicalItem,
        });
      } else if (this.#projector().consumeReaped(rowKey, placeholderClientId(reconcile.id))) {
        // The placeholder op expired unsettled (the settle reaper dropped it —
        // this create outlived the deadline), NOT a user delete. The server
        // create SUCCEEDED, so fold the canonical; taking the late-delete
        // branch here would delete a real event the user asked for. No ghost
        // promotion — the reap already un-painted the placeholder.
        this.#projector().confirm(rowKey, placeholderClientId(reconcile.id), {
          kind: "upsert",
          id: canonicalItem.id,
          item: canonicalItem,
        });
      } else {
        // Mid-flight delete: a `removeLocal` dropped the placeholder op while
        // this create was in flight (its cell already cleared, the canonical
        // never added optimistically). Honor it — remove the created event on
        // the server. Posts to the per-calendar `rowKey` the create used.
        Tracing.event("warn", "event-ops/create.late-delete", {
          placeholderId: reconcile.id,
          serverEventId: canonical.id,
        });
        // The create itself succeeded, so a refused late delete is reported
        // (the event stays on the server, visible after the next sync) rather
        // than failing the create the caller is awaiting.
        try {
          // after-failed-write: a late delete of a create no local state shows any more; the next sync reads what stayed.
          await EventWriteError.check(
            "delete",
            "POST",
            CELLS_PATH,
            () =>
              this.#table.api.postResult(CELLS_PATH, {
                path: { rowKey: RowNetworkKey.fromWire(rowKey) },
                body: Network.CellWriteRequest.make({
                  id: CellId.of(canonical.id),
                  date: EventsTable.eventCellDate(
                    canonical.startDate ?? null,
                    canonical.start,
                    "point",
                  ),
                  data: null,
                  endDate: null,
                }),
              }),
          );
        } catch (err) {
          Tracing.err("net")("event-ops/create.late-delete-failed", {
            serverEventId: canonical.id,
            error: String(err),
          });
        }
      }
    } else {
      // Plain create (no placeholder): apply an insert op, then confirm it with
      // the same canonical.
      const cid = this.#projector().nextClientId();
      this.#projector().apply(
        rowKey,
        this.#table.rowSnapshot(rowKey),
        { kind: "insert-event", item: canonicalItem },
        cid,
        actor,
      );
      this.#projector().confirm(rowKey, cid, {
        kind: "upsert",
        id: canonicalItem.id,
        item: canonicalItem,
      });
    }
    return this.#table.itemFor(canonical.id) ?? canonicalItem;
  }

  /** Field-level update, CHECKED: the patch is applied optimistically, then
   *  POSTed through the cells route and the server's answer awaited. Returns
   *  the updated item. A refusal (or no answer) drops the optimistic op — the
   *  event paints as it was — and throws `EventWriteError("update")` carrying
   *  the server's status and reason. Callers resolve the prev item via the
   *  row's facade and pass it in; EventOps does not consult `EventsTable` for
   *  prev-lookup.
   *
   *  `prevItem` is what the write is sent against (the caller's view, with
   *  its own pending writes replayed); `confirmed` is the event as the
   *  caller knows the server holds it — no pending write in it. Invariant
   *  (vi): the overlay's base is built only from `confirmed`, never from
   *  `prevItem`, so a write the caller still shows as pending (one the
   *  overlay already dropped, whose rejection has not reached the caller)
   *  can never become the confirmed base. A caller with no pending model of
   *  its own passes the held event for both.
   *
   *  The browser aborts a plain in-flight POST at `pagehide`, so an update
   *  issued just before a reload can be lost ("moved event pops back"). A drag
   *  settle that must survive the reload uses `updateDurable`. */
  async update(
    id: EventId,
    patch: Network.EventUpdate,
    prevItem: EventItem,
    confirmed: EventItem,
    actor: Actor,
  ): Promise<EventItem> {
    const prepared = this.#applyUpdate(id, patch, prevItem, confirmed, actor);
    try {
      await EventWriteError.check(
        "update",
        "POST",
        CELLS_PATH,
        () =>
          this.#table.api.postResult(CELLS_PATH, {
            path: { rowKey: RowNetworkKey.fromWire(prepared.rowKey) },
            body: prepared.body,
          }),
      );
    } catch (err) {
      this.#projector().takeBack(prepared.rowKey, prepared.clientId);
      // after-failed-write: the ops layer's caller recovers — EventsClient.update re-reads the event's row.
      throw err;
    }
    this.#confirmUpdate(prepared);
    return prepared.updated;
  }

  /**
   * Field-level update, DURABLE: the drag-settle write. Applied
   * optimistically like `update`, but persisted through the service-worker
   * debounce handoff (the same handoff cell edits use) instead of a POST the
   * browser aborts at `pagehide`. The service worker owns the write — 0ms is
   * a durable execution handoff, not a delay — and flushes it across a
   * reload.
   *
   * Coalescing is lossless by construction: the debounce key is the event
   * AND the set of fields the patch writes (`durableKey`). Rapid
   * re-drags (each writing the same time fields) share a key, so the
   * executor may send only the last body — which carries every field the
   * replaced ones did, with newer values. A rename and a move write
   * different fields, never share a key, and are sent as separate requests,
   * so neither body can drop the other's field.
   *
   * Every handoff carries its own `requestId`
   * (`TypedApi.DebounceOptions.requestId`); an executor reports a refusal
   * with the ids of the writes coalesced into the execution that was refused
   * (`DebouncedRequests.Failure.requestIds`). A refusal therefore reaches
   * exactly those writes — never an earlier or later request for the same
   * event that was dispatched separately and has its own outcome.
   *
   * Returns synchronously: `applied` is the updated item, already painted.
   * `settled` resolves once the handoff returns with no refusal reported for
   * this write, and rejects with `EventWriteError("update")` — this write's
   * own op dropped, every other write of the event kept — when the handoff
   * fails or a refusal naming this write reaches
   * `EventsTable.reportDurableFailure` first. The handoff returns no
   * response body, so a refusal only arrives that way (the client routes
   * every failure its `ClientConfig.debounceExecutor` binding reports there).
   *
   * Invariants (each executor — the service worker, the page fallback —
   * upholds its half; `handOffDurable` states the SDK's):
   * - (i) landed patches fold in issue order, also across the settle
   *   reaper's expiry (`Overlay.ack` at the entry's issue position).
   * - (ii) `settled` settles on the verdict of the execution that carried
   *   this write: the handoff answers when THAT execution finishes (the
   *   debounce's per-execution completion), and the refusal names it
   *   (`Coalesced.take` membership).
   * - (iii) a refusal reaches every write its execution carried before any
   *   of them settles: the executor reports inside the execution, and the
   *   bridge reports on the first reply of the execution to arrive.
   * - (iv) a write whose transport gives no verdict settles as UNANSWERED,
   *   never as accepted: the handoff throws, or the executor reports status
   *   `0` for it (a failed fetch; the bridge's worker timeout or transport
   *   failure), and `settled` rejects with `EventWriteError` whose `status`
   *   is `null`, this write's op dropped. Unanswered is not refused — the
   *   worker may still persist it — so the caller re-reads the row
   *   (`EventsClient.updateDurable` does).
   * - (v) the settle reaper's expiry never lets an unanswered write's
   *   fields into the overlay's confirmed base: an expired op keeps the
   *   event held (`Overlay.rebase` / `setBase`) until its answer settles it.
   * - (vi) the overlay's base is built only from the caller's CONFIRMED
   *   state (`confirmed`), never from the item the write is sent against
   *   (`prevItem`, which carries the caller's pending writes): no pending or
   *   refused write of the upper model can become the lower confirmed base,
   *   whatever order the two models settle in.
   */
  updateDurable(
    id: EventId,
    patch: Network.EventUpdate,
    prevItem: EventItem,
    confirmed: EventItem,
    actor: Actor,
  ): EventOps.DurableUpdate {
    const prepared = this.#applyUpdate(id, patch, prevItem, confirmed, actor);
    return {
      applied: Durable.shown(prepared.updated),
      settled: this.#persistDurable(id, prepared),
    };
  }

  /** Hand the prepared update to the service worker; confirm or drop it on the verdict. */
  async #persistDurable(id: string, prepared: PreparedUpdate): Promise<void> {
    const key = durableKey(prepared.rowKey, id, prepared.op.patch);
    const requestId = this.#table.nextDurableRequestId();
    let failure: Option<DebouncedRequests.Failure>;
    try {
      failure = await this.#table.handOffDurable(key, requestId, () =>
        this.#table.api.debounce.post(
          "/api/rows/{rowKey}/cells",
          {
            path: { rowKey: RowNetworkKey.fromWire(prepared.rowKey) },
            body: prepared.body,
            options: { key, requestId },
          },
          0,
        )
      );
    } catch (err) {
      this.#projector().takeBack(prepared.rowKey, prepared.clientId);
      // after-failed-write: the ops layer's caller recovers — EventsClient.updateDurable re-reads the event's row.
      // A request's failure keeps its own request; a disposed client or
      // sheet is rethrown as is; anything else the handoff threw is the
      // cells POST's transport giving no verdict (invariant (iv)).
      if (err instanceof SdkRequestError) throw EventWriteError.from("update", err);
      if (err instanceof ClientDisposedError || err instanceof SheetHandleDisposedError) throw err;
      const unanswered = new EventWriteError({
        step: "update",
        method: "POST",
        path: CELLS_PATH,
        failure: { outcome: "unanswered" },
        details: err instanceof Error ? err.message : String(err),
      });
      unanswered.cause = err;
      throw unanswered;
    }
    if (failure != null) {
      this.#projector().takeBack(prepared.rowKey, prepared.clientId);
      throw new EventWriteError({
        step: "update",
        method: "POST",
        path: CELLS_PATH,
        // `0` is the executors' "no answer" — the fetch failed, or the
        // bridge's reply from the worker never came (invariant (iv)); a 2xx
        // the executor refused (a redirect to the login page) is malformed.
        failure: DurableHandoffs.verdict(failure),
        reason: failure.reason ?? "",
        details: failure.details ?? "",
      });
    }
    this.#confirmUpdate(prepared);
  }

  /** The optimistic half of `update` / `updateDurable`: append the update op
   *  to the overlay (the projector reconciles the cell store to the new
   *  derive, with the projection's own `prev` — no caller-prev split-brain),
   *  emit a timezone change, and build the wire body. */
  #applyUpdate(
    id: EventId,
    patch: Network.EventUpdate,
    prevItem: EventItem,
    confirmed: EventItem,
    actor: Actor,
  ): PreparedUpdate {
    Tracing.record("event-ops/update.id", id);
    Tracing.record("event-ops/update.patchKeys", Object.keys(patch));
    const previousTz = prevItem.inner.timezone;
    const { updated } = EventPatch.paintWire(prevItem, patch);
    const { rowKey } = this.#newEventTarget(prevItem.inner.authId, prevItem.inner.calendarId);
    const target = EventCellWrite.target(rowKey, prevItem.inner.authId, prevItem.inner.calendarId);
    const op: EventOverlayProjector.UpdateOp = {
      kind: "update-event",
      id,
      patch,
      authId: prevItem.inner.authId,
      calendarId: target.calendarId,
    };
    const clientId = this.#projector().nextClientId();
    this.#projector().applyUpdate(
      rowKey,
      this.#table.rowSnapshot(rowKey),
      op,
      clientId,
      confirmed,
      actor,
    );

    // Timezone-change invalidation — the optimistic state now reflects the
    // new tz, so notify before the network round-trip.
    if (previousTz !== updated.inner.timezone) {
      this.#table.invalidation.emit(
        new EventTimezoneChanged(
          actor,
          id,
          previousTz ?? null,
          updated.inner.timezone ?? null,
        ),
      );
    }

    return {
      updated,
      rowKey,
      clientId,
      op,
      body: Network.CellWriteRequest.make({
        id: CellId.of(id),
        date: EventsTable.eventCellDate(
          updated.inner.startDate ?? null,
          updated.inner.start,
          "point",
        ),
        data: EventCellWrite.update(target, patch),
        endDate: null,
      }),
    };
  }

  /** Settle a landed update: fold its own field-level patch into the
   *  overlay's base, in issue order (`EventOverlayProjector.ack`). Never the
   *  whole `updated` item captured when the write was issued — that snapshot
   *  carries the optimistic fields of every write in flight at the time, so
   *  installing it on a reversed answer (or after an earlier write was
   *  refused) would restore stale fields over surviving writes. The
   *  response is not folded (the durable handoff has none, and the server
   *  stores exactly the sent fields); the next sync folds the canonical/etag. */
  #confirmUpdate(prepared: PreparedUpdate): void {
    this.#projector().ack(prepared.rowKey, prepared.clientId, prepared.op);
  }

  /** Delete an event — clears the local cell + POSTs the deletion.
   *  Caller resolves the prev item via the row's facade. When the server
   *  refuses the delete or does not answer, the optimistic removal is
   *  dropped (the event paints again) and `EventWriteError("delete")` is
   *  thrown. */
  async delete(id: EventId, prevItem: EventItem, actor: Actor): Promise<void> {
    const raw = prevItem.inner;
    // The event's OWN calendar row (`newEventTarget`), as update and create
    // address it — never this ops' `state.rowKey`, which on a promoted
    // sheet's calendar row is the PRE-promotion key the server never knew.
    const { rowKey, origin } = this.#newEventTarget(raw.authId, raw.calendarId);
    const isRecurringMaster = raw.recurrence != null &&
      raw.recurringEventId == null;

    if (!isRecurringMaster) {
      // Overlay-authoritative simple (non-recurring) delete. Two shapes, both
      // ending in a single POST. The recurring MASTER path below routes through
      // the same overlay writer; it only adds the series-wide repaint widening.
      const proj = this.#projector();
      let confirmCid: Option<MutationClientId> = null;
      if (proj.hasPending(rowKey, placeholderClientId(id))) {
        // Optimistic-only event: its insert op is still pending (no `create`
        // confirmed it). Roll the insert back. Appending a delete op instead
        // would be UN-cancelled when that op's own `confirm` drops only the
        // delete entry, leaving the still-pending insert to resurrect the event
        // (the optimistic-created / single-card / stranded-cell delete bugs).
        proj.drop(rowKey, placeholderClientId(id));
      } else {
        // Seed the base with the held `prevItem` so a held-item delete of an
        // event ABSENT from this row's snapshot (the context-menu held-item
        // `delete` path: a ghost that renders from `canonicalCells` but never entered
        // this index) still diffs to a clear. An already-indexed event is in the
        // snapshot under the same instance, so the union is harmless.
        const base = new Map(this.#table.rowSnapshot(rowKey));
        if (!base.has(id)) base.set(id, prevItem);
        confirmCid = proj.nextClientId();
        proj.apply(rowKey, base, { kind: "delete-event", id }, confirmCid, actor);
      }
      try {
        await this.#postDelete(rowKey, id, raw);
      } catch (err) {
        // Refused: un-apply the optimistic delete op so the event paints
        // again. (An optimistic-only event whose insert was rolled back above
        // stays gone — it never reached the server.)
        if (confirmCid != null) proj.takeBack(rowKey, confirmCid);
        // after-failed-write: the ops layer's caller recovers — EventsClient.delete re-reads the event's row.
        throw err;
      }
      if (confirmCid != null) {
        proj.confirm(rowKey, confirmCid, { kind: "delete", id });
      }
      return;
    }

    // Recurring MASTER delete (the whole series — "all" scope). Routed through the
    // same single overlay writer as the simple path: the master's ONE cell clears
    // optimistically, and the projector's `emitForWrites` widens the repaint to
    // `[start, MAX]` via the master's derived `RecurrenceInfo.Parent`, so the
    // timeline re-queries (and drops) every future occurrence. Occurrences are a
    // read-side expansion (the sheets `EventsIndex`), not cells the SDK index
    // holds — so clearing the one master cell + the widened emit IS the whole
    // series delete (no synthetic-children snapshot / hand-rolled emit needed).
    const masterItem = EventItem.of(raw, origin);
    // Seed the master carrying its derived `Parent` (undefined recurrenceInfo) so
    // the diff's `prev` carries it and `_timepointRange` widens to `[start, MAX]` —
    // independent of whether the master is currently indexed (held-item delete).
    const base = new Map(this.#table.rowSnapshot(rowKey));
    base.set(id, masterItem);
    const cid = this.#projector().nextClientId();
    this.#projector().apply(rowKey, base, { kind: "delete-event", id }, cid, actor);

    // Defensive: drop any synthetic occurrences that leaked into the SDK index via
    // the no-`rh` in-memory ingest path (the canonical expander is the sheets
    // `EventsIndex`, already covered by the master clear + widened emit above).
    // Idempotent — returns `[]` when the index holds none.
    this.#table.removeRecurrenceChildren(this.#state, id);

    try {
      await this.#postDelete(rowKey, id, raw);
    } catch (err) {
      this.#projector().takeBack(rowKey, cid);
      // after-failed-write: the ops layer's caller recovers — EventsClient.delete re-reads the event's row.
      throw err;
    }
    this.#projector().confirm(rowKey, cid, { kind: "delete", id });
  }

  /** POST the id-addressed `data: null` delete to `rowKey`; throws `EventWriteError("delete")`. */
  async #postDelete(rowKey: RowNetworkKey, id: string, raw: EventItem["inner"]): Promise<void> {
    // after-failed-write: the ops layer's caller recovers — EventsClient.delete re-reads the event's row.
    await EventWriteError.check(
      "delete",
      "POST",
      CELLS_PATH,
      () =>
        this.#table.api.postResult(CELLS_PATH, {
          path: { rowKey },
          body: Network.CellWriteRequest.make({
            id: CellId.of(id),
            date: EventsTable.eventCellDate(
              raw.startDate ?? null,
              raw.start,
              "point",
            ),
            data: null,
            endDate: null,
          }),
        }),
    );
  }

  /** Remove a create's placeholder from local state only (no server
   *  roundtrip): the optimistic-insert pattern drops it when the `create`
   *  failed. */
  removeLocal(placeholder: EventItem): void {
    // Overlay-authoritative rollback: drop the placeholder's pending insert op
    // (keyed by its id) from the row `insertOptimistic` projected it onto —
    // the event's OWN calendar row (`newEventTarget`), which is not this ops'
    // `state.rowKey` when a default ops created onto a specific calendar
    // (dropping there left a refused create's placeholder painted). The
    // projector re-derives (placeholder gone) and clears its cell + emits.
    const [authId, calendarId] = placeholder.acid;
    const { rowKey } = this.#newEventTarget(authId, calendarId);
    this.#projector().drop(rowKey, placeholderClientId(placeholder.id));
  }

  /** Copy an event to a different calendar — original stays put,
   *  a fresh event is created in the target. Nothing is applied locally
   *  until the server answers; a refusal throws `EventWriteError("copy-to")`. */
  async copyTo(
    eventId: EventId,
    targetAuthId: AuthId,
    targetCalendarId: CalendarId,
    sourceItem: EventItem,
    actor: Actor,
  ): Promise<EventItem> {
    const source = sourceItem.inner;
    // Project optimism onto the TARGET calendar's row (the POST target), not this
    // ops' `state.rowKey` — see `newEventTarget`. Mismatch = no live paint.
    const { rowKey, origin } = this.#newEventTarget(targetAuthId, targetCalendarId);
    const target = EventCellWrite.target(rowKey, targetAuthId, targetCalendarId);
    const sourceTarget = EventCellWrite.target(
      this.#newEventTarget(source.authId, source.calendarId).rowKey,
      source.authId, source.calendarId,
    );

    // after-failed-write: the ops layer's caller recovers — EventsClient.copyTo re-reads the target calendar's row.
    const response = await EventWriteError.check(
      "copy-to",
      "POST",
      CELLS_PATH,
      () =>
        this.#table.api.postResult(CELLS_PATH, {
          path: { rowKey: RowNetworkKey.fromWire(rowKey) },
          body: Network.CellWriteRequest.make({
            date: EventsTable.eventCellDate(
              source.startDate ?? null,
              source.start,
              "day",
            ),
            data: EventCellWrite.copy(target, sourceTarget, eventId),
            endDate: null,
            id: null,
          }),
        }),
    );

    const raw = response.cells[0]?.data;
    if (!raw) {
      throw malformedAck("copy-to", "copyTo: the ack carried no cell");
    }
    const canonical = canonicalEvent(this.#state.rh?.decodeCell(raw));
    if (!canonical) {
      throw malformedAck("copy-to", "copyTo: the ack's cell is not an event");
    }

    // copyTo mints a NEW event — apply an insert op for the server canonical, then
    // confirm it. The projector writes the cell on the TARGET calendar's row +
    // mirrors the canonical lane.
    const canonicalItem = EventItem.of(canonical, origin);
    const cid = this.#projector().nextClientId();
    this.#projector().apply(
      rowKey,
      this.#table.rowSnapshot(rowKey),
      { kind: "insert-event", item: canonicalItem },
      cid,
      actor,
    );
    this.#projector().confirm(rowKey, cid, {
      kind: "upsert",
      id: canonicalItem.id,
      item: canonicalItem,
    });
    return this.#table.itemFor(canonical.id) ?? canonicalItem;
  }

  /** Move an event to a different calendar — uses Google's native
   *  move API, preserves event id and extended properties. Nothing is
   *  re-indexed locally until the server answers; a refusal throws
   *  `EventWriteError("move-to")`. */
  async moveEvent(
    eventId: EventId,
    targetAuthId: AuthId,
    targetCalendarId: CalendarId,
    prevItem: EventItem,
    actor: Actor,
  ): Promise<EventItem> {
    const table = this.#table;
    const sourceTarget = EventCellWrite.target(
      this.#newEventTarget(prevItem.inner.authId, prevItem.inner.calendarId).rowKey,
      prevItem.inner.authId, prevItem.inner.calendarId,
    );
    const target = EventCellWrite.target(
      this.#newEventTarget(targetAuthId, targetCalendarId).rowKey,
      targetAuthId, targetCalendarId,
    );
    // Wait for the native move ACK before touching local cells. The server
    // preserves the id; only its old and new row addresses change.
    // after-failed-write: the ops layer's caller recovers — EventsClient.moveTo re-reads both calendars' rows.
    const response = await EventWriteError.check(
      "move-to",
      "POST",
      CELLS_PATH,
      () =>
        this.#table.api.postResult(CELLS_PATH, {
          path: { rowKey: RowNetworkKey.fromWire(sourceTarget.rowKey) },
          body: Network.CellWriteRequest.make({
            id: CellId.of(eventId),
            date: EventsTable.eventCellDate(
              prevItem.inner.startDate ?? null,
              prevItem.inner.start,
              "point",
            ),
            data: EventCellWrite.move(sourceTarget, target),
            endDate: null,
          }),
        }),
    );

    const raw = response.cells[0]?.data;
    if (!raw) {
      throw malformedAck("move-to", "moveEvent: the ack carried no cell");
    }
    const canonical = canonicalEvent(this.#state.rh?.decodeCell(raw));
    if (!canonical) {
      throw malformedAck("move-to", "moveEvent: the ack's cell is not an event");
    }
    // Reconcile both cell-backed readers, not only the global id index. A
    // source-row title scan otherwise finds the old cell first and hands a
    // source-origin item to a later delete. For an attached source use its
    // address-aware clear (also handles a standard cell stranded on a multi
    // row); otherwise the canonical bridge clears the source handle by id.
    if (sourceTarget.rowKey === target.rowKey) {
      table.writeCanonicalForKey(target.rowKey, canonical, eventId, prevItem.inner);
    } else {
      const sourceState = table.getRowStateByKey(sourceTarget.rowKey);
      if (sourceState != null) {
        table.applyOptimisticDelete(sourceState, prevItem.inner, eventId);
      } else {
        table.writeCanonicalForKey(sourceTarget.rowKey, null, eventId, prevItem.inner);
      }
      table.writeCanonicalForKey(target.rowKey, canonical, eventId, null);
    }
    // The target can be unattached to this table; keep id resolution and
    // held-item mutation routing authoritative in either case.
    table.ingestCanonicalForKey(target.rowKey, canonical, eventId);
    const newItem = nonnull(
      table.itemFor(eventId),
      "EventOps.moveEvent: moved event missing from the table",
    );

    emitEventUpdate(this.#table, prevItem, newItem, actor);

    return newItem;
  }
}

export namespace EventOps {
  /** What `updateDurable` returns (`Durable`): the event with the patch
   *  applied, as it now paints, and the write's verdict — `settled` rejects
   *  with `EventWriteError("update")` once the change was taken back,
   *  `outcome` `"refused"` (the server's status) or `"unanswered"`
   *  (invariant (iv); never resolved as accepted). */
  export type DurableUpdate = Durable<EventItem>;
}

export type { EventItem, EventItemTimed, EventItemAllDay };

/**
 * The failure of an event write the server ACCEPTED but whose ack the SDK
 * cannot read as the event it wrote (no cell, or a cell that is not an
 * event): `EventWriteError` with outcome `"malformed"` — the server answered
 * 2xx, so the write may be stored (`SdkRequestError.mayHaveStored`) and the
 * caller re-reads rather than retrying blind.
 */
function malformedAck(step: EventWriteError.Step, details: string): EventWriteError {
  return new EventWriteError({
    step,
    method: "POST",
    path: CELLS_PATH,
    failure: { outcome: "malformed" },
    details: `EventOps.${details}`,
  });
}

/**
 * The event a write response's cell decodes to — the row's encoding decodes
 * the wire cell, and `EVENT_SHAPE` checks the result is an `Event` — or
 * `null` when it is not one.
 */
function canonicalEvent(decoded: Option<Json | CellValue>): Option<CalendarEvent> {
  return decoded != null && EVENT_SHAPE.probe(decoded) ? decoded : null;
}
